/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Organization, OrgPlan, PLAN_LIMITS, PlanFeature, PlanLimit, SubscriptionPlan } from '../domain/entities';

/**
 * Resolve the effective PlanLimit for an organisation.
 * If the org has a subscriptionPlanId and a matching plan exists, use its features.
 * Otherwise fall back to the hardcoded PLAN_LIMITS keyed by org.plan.
 */
function resolveFeatures(
  org: Organization,
  subscriptionPlans?: SubscriptionPlan[]
): PlanLimit {
  if (org.subscriptionPlanId && subscriptionPlans) {
    const sp = subscriptionPlans.find(p => p.id === org.subscriptionPlanId);
    if (sp) return sp.features;
  }
  return PLAN_LIMITS[org.plan];
}

/**
 * Returns true if the organisation's plan includes the given feature.
 * Super Admin context (org = null) always returns true so the SA can see everything.
 * When subscriptionPlans are provided, the dynamic plan is checked first.
 * Special: `maintenanceAccess` also honours the org-level manual override.
 */
export function canUsePlanFeature(
  org: Organization | null | undefined,
  feature: PlanFeature,
  isSuperAdmin = false,
  subscriptionPlans?: SubscriptionPlan[]
): boolean {
  if (isSuperAdmin) return true;
  if (!org) return false;
  // Manual org-level override for maintenance
  if (feature === 'maintenanceAccess' && org.maintenanceOverride) return true;
  const features = resolveFeatures(org, subscriptionPlans);
  return features[feature] === true;
}

/**
 * Returns a human-readable upgrade prompt for the minimum plan required.
 */
export function getPlanUpgradeMessage(
  feature: PlanFeature,
  currentPlan: OrgPlan
): string {
  const upgradeMap: Record<PlanFeature, OrgPlan> = {
    apiAccess:        OrgPlan.PRO,
    webhookAccess:    OrgPlan.PRO,
    analyticsAccess:  OrgPlan.BASIC,
    systemLogsAccess: OrgPlan.BASIC,
    whiteLabelAccess: OrgPlan.ENTERPRISE,
    maintenanceAccess: OrgPlan.PRO,
    marketScoutAccess: OrgPlan.PRO,
  };
  const required = upgradeMap[feature];
  return `This feature requires the ${required} plan. Your organisation is on the ${currentPlan} plan.`;
}

/**
 * Returns true when the organisation has reached (or exceeded) a numeric usage limit.
 * The metricId corresponds to a key in PlanLimit.usageLimits (e.g. 'max_users', 'max_riders').
 */
export function isAtPlanLimit(
  org: Organization | null | undefined,
  metricId: string,
  currentCount: number,
  subscriptionPlans?: SubscriptionPlan[]
): boolean {
  if (!org) return true;
  const features = resolveFeatures(org, subscriptionPlans);
  const limit = features.usageLimits[metricId];
  if (limit === undefined || limit === null || limit < 0 || !Number.isFinite(limit)) return false;
  return currentCount >= limit;
}

/** Human-readable label for each plan. */
export const PLAN_LABELS: Record<OrgPlan, string> = {
  [OrgPlan.FREE]:       'Starter',
  [OrgPlan.BASIC]:      'Basic',
  [OrgPlan.PRO]:        'Pro',
  [OrgPlan.ENTERPRISE]: 'Enterprise',
};

/** Tailwind colour classes for plan badges. */
export const PLAN_BADGE_COLORS: Record<OrgPlan, string> = {
  [OrgPlan.FREE]:       'bg-zinc-100 text-zinc-600',
  [OrgPlan.BASIC]:      'bg-blue-100 text-blue-700',
  [OrgPlan.PRO]:        'bg-indigo-100 text-indigo-700',
  [OrgPlan.ENTERPRISE]: 'bg-amber-100 text-amber-700',
};
